import { Row, Col } from 'react-bootstrap'
import RichTextInput from '../richTextInput/RichTextInput'
import DimensionInput from '../dimensionInput/DimensionInput'
import FilesUploader from '../filesUploader/FilesUploader'
import { AttachmentDto } from 'api/common/models/AttachmentDto'
import MoneyInput from '../moneyInput/MoneyInput'
import InputError from '../inputError/InputError'
import { ParameterMinMax, ParameterPrice, ParameterProductLine , ElementParameterDto } from 'api/elements/Models/ElementDto'
import { SortableTree } from '../sortableTree/SortableTree'
import ConfigurableElementsLine from '../configurableElementsLine/ConfigurableElementsLine'
import { ElementParameterComponentDataProvider } from './ElementParameterComponentDataProvider'
import { FlattenedItem } from '../sortableTree/types'
import { AtLeast } from 'common/types/Atleast'
import { useTranslation } from 'react-i18next'
import styles from './ElementParametersComponent.module.scss'
import Button from '../button/Button'
import { IoAddSharp } from 'react-icons/io5'
import { newProductLine, onMove } from './utils'
import { useContext, useEffect, useMemo, useState } from 'react'
import SuppliersService from 'api/suppliers/SuppliersService'
import UnitsService from 'api/units/UnitsService'
import SelectInput, { SelectInputOption } from '../selectInput/SelectInput'
import DateInput from '../dateInput/DateInput'
import ExpressionSystem, { ExpressionParameter } from '../expressionButton/ExpressionSystem'

import { ParameterSectionParameterType, ParameterSectionType } from '../parametersSections/models'
import type { CatalogParameterDto } from 'api/catalogParameters/models/CatalogParameterDto'

const buildCircularReferenceValidator = (
    allParameters: ElementParameterDto[],
    parameterKey: string,
    t: (key: string) => string
): ((expression: string) => string | null) => {
    return (expression: string) => {
        const currentParam = allParameters.find(p => p.key === parameterKey);
        if (!currentParam) return null;

        const currentId = ExpressionSystem.formatParameterId([currentParam.familyParameterId || currentParam.id || currentParam.key]);
        const currentMentionId = ExpressionSystem.serializeParameterMentionId(currentId);

        const formulasMap = new Map<string, string | null>();
        allParameters
            .filter(p =>
                p.key !== parameterKey &&
                p.active !== false &&
                p.type === ParameterSectionType.PARAMETER &&
                [ParameterSectionParameterType.NUMBER, ParameterSectionParameterType.QUANTITY, ParameterSectionParameterType.PRICE].includes(p.parameterType as ParameterSectionParameterType)
            )
            .forEach(p => {
                const mentionId = ExpressionSystem.serializeParameterMentionId(
                    ExpressionSystem.formatParameterId([p.familyParameterId || p.id || p.key])
                );
                formulasMap.set(mentionId, p.formulaExpression ?? null);
            });

        if (ExpressionSystem.hasCircularReference(currentMentionId, expression, formulasMap)) {
            return t('common.formula_circular_reference');
        }
        return null;
    };
};

interface Props {
    required?: boolean,
    disabled: boolean;
    productsFamilyId?: string
    hasSubmited: boolean;
    selectedSectionKey?: string
    onClick?: (key: string) => void;
}

const getElementExpressionParameters = (
    allParameters: ElementParameterDto[],
    currentKey?: string,
    catalogParameters?: CatalogParameterDto[],
    t?: (key: string) => string
): ExpressionParameter[] => {
    const filteredParams = allParameters
        .filter(p =>
            p.key !== currentKey &&
            p.active !== false &&
            [ParameterSectionParameterType.NUMBER, ParameterSectionParameterType.QUANTITY, ParameterSectionParameterType.PRICE].includes(p.parameterType as ParameterSectionParameterType)
        );

    const result: ExpressionParameter[] = filteredParams
        .map(p => ({
            // Use familyParameterId to match family expressions format (family uses p.id which becomes familyParameterId in element)
            id: ExpressionSystem.formatParameterId([p.familyParameterId || p.id || p.key]),
            name: ExpressionSystem.formatParameterName([p.familyName || '', p.parameterName || '']),
            value: p.number ?? p.quantity ?? p.price?.price ?? null
        }));

    // Also register catalog parameter IDs so formulas imported from catalog resolve correctly
    for (const p of filteredParams) {
        if (p.catalogParameterId) {
            const catalogId = ExpressionSystem.formatParameterId([p.catalogParameterId]);
            if (!result.some(r => r.id === catalogId)) {
                result.push({
                    id: catalogId,
                    name: ExpressionSystem.formatParameterName([p.familyName || '', p.parameterName || '']),
                    value: p.number ?? p.quantity ?? p.price?.price ?? null
                });
            }
        }
    }

    // Add catalog parent line parameter references (display-only, value=0 in element context)
    if (t && catalogParameters && catalogParameters.length > 0) {
        const budgetLabel = t('budgets.budget.budget');
        const parentLineLabel = t('catalog_parameters.parent_line_label');
        const numericTypes = ['NUMBER', 'QUANTITY', 'PRICE', 'NUMBER_MIN_MAX', 'WEIGHT'];
        catalogParameters
            .filter(cp => cp.parameterType && numericTypes.includes(cp.parameterType))
            .forEach(cp => {
                result.push({
                    id: ExpressionSystem.formatParameterId(['CURRENT_PARENT_LINE', 'CATALOG', cp.id, 'parameter']),
                    name: ExpressionSystem.formatParameterName([budgetLabel, parentLineLabel, cp.name]),
                    value: 0,
                });
            });
    }

    // Append composition (PRODUCTS) parameter pricing fields
    if (t) {
        const productsParams = allParameters
            .filter(p =>
                p.key !== currentKey &&
                p.active !== false &&
                p.parameterType === ParameterSectionParameterType.PRODUCTS
            );
        const compositionFields: { fieldKey: string; translationKey: string }[] = [
            { fieldKey: 'totalExcludingTax', translationKey: 'budgets.budget.lines.header.total_without_tax' },
            { fieldKey: 'totalIncludingTax', translationKey: 'budgets.budget.lines.header.total_with_tax' },
        ];
        for (const p of productsParams) {
            for (const field of compositionFields) {
                result.push({
                    id: ExpressionSystem.formatParameterId([p.familyParameterId || p.id || p.key, 'composition', field.fieldKey]),
                    name: ExpressionSystem.formatParameterName([p.familyName || '', p.parameterName || '', t(field.translationKey)]),
                    value: 0,
                });
            }
        }
    }

    // Add budget field options (display-only, value=0 in element context) so formulas from families resolve
    if (t) {
        const budgetLabel = t('budgets.budget.budget');
        const currentLineLabel = t('catalog_parameters.current_line_label');
        const sectionLabel = t('budgets.budget.lines.add_options.section');
        const BUDGET_FIELD_KEYS: { fieldKey: string; translationKey: string; valueType: 'number' | 'string' }[] = [
            { fieldKey: 'quantity', translationKey: 'budgets.budget.lines.header.quantity_tooltip', valueType: 'number' },
            { fieldKey: 'grossUnitPrice', translationKey: 'budgets.budget.lines.header.gross_unit_price', valueType: 'number' },
            { fieldKey: 'marginPercentage', translationKey: 'budgets.budget.lines.header.margin', valueType: 'number' },
            { fieldKey: 'discountPercentage', translationKey: 'budgets.budget.lines.header.discount', valueType: 'number' },
            { fieldKey: 'unitPrice', translationKey: 'budgets.budget.lines.header.price_per_unity_tooltip', valueType: 'number' },
            { fieldKey: 'totalUnitExcludingTax', translationKey: 'budgets.budget.lines.header.total_unit_without_tax_tooltip', valueType: 'number' },
            { fieldKey: 'unitPriceWithChildren', translationKey: 'budgets.budget.lines.header.unit_price_with_children', valueType: 'number' },
            { fieldKey: 'absoluteChildrenSum', translationKey: 'budgets.budget.lines.header.absolute_children_sum', valueType: 'number' },
            { fieldKey: 'totalExcludingTax', translationKey: 'budgets.budget.lines.header.total_without_tax', valueType: 'number' },
            { fieldKey: 'totalIncludingTax', translationKey: 'budgets.budget.lines.header.total_with_tax', valueType: 'number' },
            { fieldKey: 'unitSymbol', translationKey: 'budgets.budget.lines.header.unity_tooltip', valueType: 'string' },
        ];
        for (const field of BUDGET_FIELD_KEYS) {
            const fieldLabel = t(field.translationKey);
            const defaultValue = field.valueType === 'string' ? '' : 0;
            result.push({
                id: ExpressionSystem.formatParameterId(['BUDGET_FIELD_ELEMENT', field.fieldKey]),
                name: ExpressionSystem.formatParameterName([budgetLabel, currentLineLabel, fieldLabel]),
                value: defaultValue,
            });
            result.push({
                id: ExpressionSystem.formatParameterId(['BUDGET_FIELD_SECTION', field.fieldKey]),
                name: ExpressionSystem.formatParameterName([budgetLabel, sectionLabel, fieldLabel]),
                value: defaultValue,
            });
        }

        // - "Orçamento > Linha pai > Campo" → value from the nearest element/component ancestor
        const parentLineFieldLabel = t('catalog_parameters.parent_line_label');
        for (const field of BUDGET_FIELD_KEYS) {
            if (field.valueType === 'string') continue;
            const fieldLabel = t(field.translationKey);
            result.push({
                id: ExpressionSystem.formatParameterId(['BUDGET_FIELD_PARENT', field.fieldKey]),
                name: ExpressionSystem.formatParameterName([budgetLabel, parentLineFieldLabel, fieldLabel]),
                value: 0,
            });
        }
    }

    return result;
};

const catalogParametersCache: { data: CatalogParameterDto[] | null; promise: Promise<CatalogParameterDto[]> | null } = { data: null, promise: null };

const loadCatalogParameters = (): Promise<CatalogParameterDto[]> => {
    if (catalogParametersCache.data) return Promise.resolve(catalogParametersCache.data);
    if (catalogParametersCache.promise) return catalogParametersCache.promise;
    catalogParametersCache.promise = import('api/catalogParameters/CatalogParametersService')
        .then(mod => mod.default.getCatalog())
        .then(data => { catalogParametersCache.data = data; return data; });
    return catalogParametersCache.promise;
};

const useElementExprParams = (allParameters: ElementParameterDto[] | undefined, parameterKey: string | undefined) => {
    const { t } = useTranslation();
    const [catalogParams, setCatalogParams] = useState<CatalogParameterDto[]>(catalogParametersCache.data ?? []);

    useEffect(() => {
        void loadCatalogParameters().then(setCatalogParams);
    }, []);

    return () => getElementExpressionParameters(allParameters || [], parameterKey, catalogParams, t);
};

export const TextParameter = ({ value,required,disabled,onChange,hasSubmited }: Props & { value?: string | null ; onChange: (x: string) => void }): JSX.Element => {
    const hasError = required && hasSubmited && value === undefined
    return (
        <Row className='mt-1 mb-2'>
            <Col>
                <RichTextInput
                    value={value}
                    onBlur={onChange}
                    onChange={onChange}
                    disabled={disabled}
                    hasError={hasError}
                />
                {hasError && <InputError error={{ type: 'required' }} />}
            </Col>
        </Row>
    )
}

export const NumberParameter = ({
    value, disabled, onChange, required, hasSubmited, unitSymbol,
    formulaExpression, allParameters, parameterKey, onFormulaChange
}: Props & {
    value?: number | null;
    onChange: (x: number | null | undefined) => void;
    unitSymbol?: string | null;
    formulaExpression?: string | null;
    allParameters?: ElementParameterDto[];
    parameterKey?: string;
    onFormulaChange?: (formula: string | null, value: number | null | undefined) => void;
}): JSX.Element => {
    const { t } = useTranslation();
    const getElementExprParams = useElementExprParams(allParameters, parameterKey);
    const hasError = required && hasSubmited && value === undefined;
    const hasFormula = !!formulaExpression;

    // Calcula um hash dos valores dos outros parâmetros para detectar mudanças
    const otherParamsHash = useMemo(() => {
        if (!allParameters) return '';
        return getElementExprParams()
            .map(p => `${p.id}:${p.value}`)
            .join(',');
    }, [allParameters, parameterKey]);

    useEffect(() => {
        if (hasFormula && allParameters && onFormulaChange) {
            const params = getElementExprParams();
            const result = ExpressionSystem.evaluate(formulaExpression, params);
            if (!result.error && result.value !== value) {
                // Recalculate formula result but keep the formula expression
                onFormulaChange(formulaExpression ?? null, result.value);
            }
        }
    }, [formulaExpression, otherParamsHash, hasFormula]);

    const handleFormulaChange = (expr: string | null, resultValue: number | null | undefined) => {
        if (onFormulaChange) {
            onFormulaChange(expr, resultValue);
        }
    };

    const handleValueChange = (newValue: number | null | undefined) => {
        // When user manually changes the value, clear the formula
        // But only if the value is actually different (to avoid clearing during formula evaluation)
        // Compare with tolerance to handle decimal rounding
        const tolerance = 0.000001;
        const currentValue = value ?? 0;
        const newVal = newValue ?? 0;
        const isDifferent = Math.abs(currentValue - newVal) > tolerance;

        if (formulaExpression && onFormulaChange && isDifferent) {
            onFormulaChange('', newValue);
        } else if (!formulaExpression) {
            onChange(newValue);
        }
    };

    return (
        <Row className='mt-1 mb-2'>
            <Col xs={12} md={5} lg={4} xxl={2} xxxl={1}>
                <MoneyInput
                    value={value}
                    unitPrefix={unitSymbol ?? undefined}
                    hideUnitsDrop={!unitSymbol}
                    disabled={disabled}
                    onChange={handleValueChange}
                    hasError={hasError}
                    decimalScale={6}
                    displayDecimalScale={2}
                    expressionButton={{
                        value: formulaExpression ?? '',
                        title: t('common.formula'),
                        disabled,
                        loadParameters: () => getElementExprParams(),
                        onChange: handleFormulaChange,
                        onValidate: allParameters && parameterKey ? buildCircularReferenceValidator(allParameters, parameterKey, t) : undefined,
                    }}
                />
                {hasError && <InputError error={{ type: 'required' }} />}
            </Col>
        </Row>
    );
}

export const MaxMinParameter = ({ item, onChange,disabled,required,productsFamilyId,hasSubmited, onClick,selectedSectionKey }: Props &
{ item: ParameterMinMax;onChange: (item: ParameterMinMax) => void; }): JSX.Element => {
    // const [selectedSectionKey, setSelectedSectionKey] = useState<string>('');

    const onChangeMin = (value: number | null | undefined) => {
        onChange({
            ...item,
            minValue: value,
        });
    }

    const onChangeMax = (value: number | null | undefined) => {
        onChange({
            ...item,
            maxValue: value,
        });
    }

    const onChangeUnit = (value: string | null | undefined) => {
        onChange({
            ...item,
            unitId: value,
        });
    }
    const onChangeProducts = (items: ParameterProductLine[]) => {
        onChange({
            ...item,
            products: items,
        });
    }

    const onChangeProduct = (newItem: AtLeast<FlattenedItem<ParameterProductLine>, 'key'>,type: 'change' | 'remove' | 'change-self-and-children') => {
        if (type === 'change') {
            onChange({
                ...item,
                products: (item?.products ?? []).map(x => {
                    if (x.key === newItem.key) {
                        return { ...x,...newItem }
                    }
                    return x
                }) ?? [],
            });
        }

        if (type === 'remove') {
            const updatedLines = (item.products ?? []).filter(line => line.key !== newItem.key);
            onChangeProducts([...updatedLines])
        }
    }

    const onAddWhenEmpty = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.stopPropagation()
        onChangeProducts([...(item.products ?? []), newProductLine()])
    }

    const onLineCreate = (newItem: ParameterProductLine) => {
        onChangeProducts([...(item.products ?? []), newItem])
    }

    const onMoveItem = (key: string, type: 'up' | 'down') => {
        const updatedLines = onMove(item.products ?? [],key,type)
        onChangeProducts([...updatedLines])
    }

    const onClickProduct = (key: string) => {
        // setSelectedSectionKey(key)
        if (onClick) {
            onClick(key)
        }
    }
    const hasError = required && hasSubmited && (item.minValue === undefined || item.maxValue === undefined || item.unitId === undefined)
    return (
        <>
            <Row className='mt-1 mb-2'>
                <Col xs={12} lg={6} xxl={5} xxxl={3}>
                    <DimensionInput
                        minValue={item.minValue}
                        maxValue={item.maxValue}
                        onChangeMinValue={onChangeMin}
                        onChangeMaxValue={onChangeMax}
                        onChangeUnit={onChangeUnit}
                        unit={item.unitId ?? ''}
                        disabled={disabled}
                        hasError={hasError}
                    />
                    {hasError && <InputError error={{ type: 'required' }} />}
                </Col>
            </Row>
            <Row>
                <Col>
                    {item.products && item.products.length > 0 && <ElementParameterComponentDataProvider.Provider value={{
                        selectedSectionKey: selectedSectionKey ?? '',
                        lines: item.products ?? [],
                        hasSubmited,
                        productsFamilyId,
                        required: false,
                        catalogParameters: [],
                    }}>
                        <SortableTree
                            items={item.products ?? []}
                            onItemsChanged={(newItems) => onChangeProducts(newItems as ParameterProductLine[])}
                            onItemChange={onChangeProduct}
                            onItemCreate={onLineCreate}
                            onMoveItem={onMoveItem}
                            onSelectItem={onClickProduct}
                            TreeItemComponent={ConfigurableElementsLine}
                            canHaveChildren={false}
                            canRootHaveChildren={true}
                            dropAnimation={null}
                            indentationWidth={20}
                            disableSorting={disabled}
                        />
                    </ElementParameterComponentDataProvider.Provider>}
                    <>{addProductDiv(item.products ?? [],disabled, false,false,onAddWhenEmpty)}</>
                </Col>
            </Row>
        </>
    )
}

export const ImagesParameter = ({ medias,disabled,onChange,required,hasSubmited }: Props & { medias: AttachmentDto[];onChange: (x: AttachmentDto[]) => void }): JSX.Element => {
    const hasError = required && hasSubmited && (medias.length === 0 || medias.every(x => x.remove === true))

    return (
        <Row className='mt-1 mb-2'>
            <Col>
                <FilesUploader
                    files={medias}
                    type='images'
                    onChange={onChange}
                    disabled={disabled}
                    containerName={(f) => f.key ?? ''}
                    hasError={hasError}
                />
                {hasError && <InputError error={{ type: 'required' }} />}
            </Col>
        </Row>
    )
}

export const ProdutParameter = ({ items,onChange,disabled,required,productsFamilyId,hasSubmited, onClick ,selectedSectionKey }: Props &
{ items: ParameterProductLine[];onChange: (items: ParameterProductLine[]) => void }): JSX.Element => {
    // const [selectedSectionKey, setSelectedSectionKey] = useState<string>('');

    const onChangeProducts = (items: ParameterProductLine[]) => {
        onChange([...items])
    }

    const onChangeProduct = (newItem: AtLeast<FlattenedItem<ParameterProductLine>, 'key'>,type: 'change' | 'remove' | 'change-self-and-children') => {
        if (type === 'change') {
            onChange(
                items.map(x => {
                    if (x.key === newItem.key) {
                        return { ...x,...newItem }
                    }
                    return x
                })
            );
        }

        if (type === 'remove') {
            const updatedLines = items.filter(line => line.key !== newItem.key);
            onChange([...updatedLines])
        }
    }

    const onAddWhenEmpty = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.stopPropagation()
        onChangeProducts([newProductLine()])
    }

    const onLineCreate = (newItem: ParameterProductLine) => {
        onChangeProducts([...items, newItem])
    }

    const onMoveItem = (key: string, type: 'up' | 'down') => {
        const updatedLines = onMove(items,key,type)
        onChangeProducts([...updatedLines])
    }

    const onClickProduct = (key: string) => {
        // setSelectedSectionKey(key)
        if (onClick) {
            onClick(key)
        }
    }

    return (
        <Row className='mt-1 mb-2'>
            <Col>
                {items.length > 0 && <ElementParameterComponentDataProvider.Provider value={{
                    selectedSectionKey: selectedSectionKey ?? '',
                    lines: items,
                    hasSubmited,
                    productsFamilyId,
                    required: false,
                    catalogParameters: [],
                }}>
                    <SortableTree
                        items={items}
                        onItemsChanged={(newItems) => onChangeProducts(newItems as ParameterProductLine[])}
                        onItemChange={onChangeProduct}
                        onItemCreate={onLineCreate}
                        onMoveItem={onMoveItem}
                        onSelectItem={onClickProduct}
                        TreeItemComponent={ConfigurableElementsLine}
                        canHaveChildren={false}
                        canRootHaveChildren={true}
                        dropAnimation={null}
                        indentationWidth={20}
                        disableSorting={disabled}
                    />
                </ElementParameterComponentDataProvider.Provider>}
                <>{addProductDiv(items,disabled,required ?? false,hasSubmited,onAddWhenEmpty)}</>
            </Col>
        </Row>
    )
}

const addProductDiv = (items: ParameterProductLine[],disabled: boolean,required: boolean,hasSubmited: boolean,onAddWhenEmpty: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void) => {
    const { t } = useTranslation()
    if (!items.length) {
        if (disabled) {
            return <div className='mt-3'>
                <div className={`${styles.noItemsText}`}>
                    {t('messages.no_info_to_show')}
                </div>
            </div>
        } else {
            return <>
                {required && hasSubmited && <InputError error={{ type: 'required' }} />}
                <Button variant={required && hasSubmited ? 'danger' : 'third'} className={`${styles.addButton}`} onClick={onAddWhenEmpty}>
                    <IoAddSharp fontSize={18} />
                    <span className='v-align-middle'>
                        {t('elements.add_product')}
                    </span>
                </Button>
            </>
        }
    }
}

export const SupplierParameter = ({ value, disabled, onChange, required, hasSubmited }: Props & {
    value?: string | null;
    onChange: (x: string | null) => void
}): JSX.Element => {
    const [suppliers, setSuppliers] = useState<SelectInputOption[]>([])
    const hasError = required && hasSubmited && !value

    useEffect(() => {
        const loadSuppliers = async () => {
            const result = await SuppliersService.getCatalog()
            setSuppliers(result.map(s => ({ value: s.id, label: s.name })))
        }
        void loadSuppliers()
    }, [])

    return (
        <Row className='mt-1 mb-2'>
            <Col xs={12} md={6} lg={4} xxl={3}>
                <SelectInput
                    options={suppliers}
                    value={value ?? null}
                    onChange={(newValue) => onChange(newValue ?? null)}
                    disabled={disabled}
                    hasError={hasError}
                />
                {hasError && <InputError error={{ type: 'required' }} />}
            </Col>
        </Row>
    )
}

export const DateParameter = ({ value, disabled, onChange, required, hasSubmited }: Props & {
    value?: string | null;
    onChange: (x: string | null) => void
}): JSX.Element => {
    const hasError = required && hasSubmited && !value

    const handleChange = (date: Date | null | undefined) => {
        if (date) {
            onChange(date.toISOString())
        } else {
            onChange(null)
        }
    }

    const dateValue = value ? new Date(value) : null

    return (
        <Row className='mt-1 mb-2'>
            <Col xs={12} md={6} lg={4} xxl={3}>
                <DateInput
                    value={dateValue}
                    onChange={handleChange}
                    disabled={disabled}
                />
                {hasError && <InputError error={{ type: 'required' }} />}
            </Col>
        </Row>
    )
}

export const PriceParameter = ({
    item, disabled, onChange, required, hasSubmited,
    formulaExpression, allParameters, parameterKey, onFormulaChange
}: Props & {
    item: ParameterPrice;
    onChange: (item: ParameterPrice) => void;
    formulaExpression?: string | null;
    allParameters?: ElementParameterDto[];
    parameterKey?: string;
    onFormulaChange?: (formula: string | null, price: number | null | undefined) => void;
}): JSX.Element => {
    const { t } = useTranslation();
    const getElementExprParams = useElementExprParams(allParameters, parameterKey);
    const [units, setUnits] = useState<SelectInputOption[]>([]);
    const hasPriceError = required && hasSubmited && (item.price === undefined || item.price === null);
    const hasUnitError = required && hasSubmited && !item.unitId;
    const hasFormula = !!formulaExpression;

    useEffect(() => {
        const loadUnits = async () => {
            const result = await UnitsService.getFormattedCatalog();
            setUnits(result.map(u => ({ value: u.id, label: u.symbol })));
        };
        void loadUnits();
    }, []);

    // Calcula um hash dos valores dos outros parâmetros para detectar mudanças
    const otherParamsHash = useMemo(() => {
        if (!allParameters) return '';
        return getElementExprParams()
            .map(p => `${p.id}:${p.value}`)
            .join(',');
    }, [allParameters, parameterKey]);

    useEffect(() => {
        if (hasFormula && allParameters && onFormulaChange) {
            const params = getElementExprParams();
            const result = ExpressionSystem.evaluate(formulaExpression, params);
            if (!result.error && result.value !== item.price) {
                // Recalculate formula result but keep the formula expression
                onFormulaChange(formulaExpression ?? null, result.value);
            }
        }
    }, [formulaExpression, otherParamsHash, hasFormula]);

    const onChangePrice = (value: number | null | undefined) => {
        // When user manually changes the value, clear the formula
        // But only if the value is actually different (to avoid clearing during formula evaluation)
        // Compare with tolerance to handle decimal rounding
        const tolerance = 0.000001;
        const currentPrice = item.price ?? 0;
        const newPrice = value ?? 0;
        const isDifferent = Math.abs(currentPrice - newPrice) > tolerance;

        if (formulaExpression && onFormulaChange && isDifferent) {
            onFormulaChange('', value);
        } else if (!formulaExpression) {
            onChange({ ...item, price: value });
        }
    };

    const onChangeUnit = (value: string | null | undefined) => {
        onChange({ ...item, unitId: value ?? null });
    };

    const handleFormulaChange = (expr: string | null, resultValue: number | null | undefined) => {
        if (onFormulaChange) {
            onFormulaChange(expr, resultValue);
        }
    };

    const selectedUnitSymbol = units.find(u => u.value === item.unitId)?.label;
    const unitSuffix = selectedUnitSymbol ? `€/${selectedUnitSymbol}` : '€';

    return (
        <Row className='mt-1 mb-2'>
            <Col xs={12} md={4} lg={3} xxl={2}>
                <MoneyInput
                    value={item.price}
                    onChange={onChangePrice}
                    disabled={disabled}
                    hasError={hasPriceError}
                    hideUnitsDrop={true}
                    unitPrefix={unitSuffix}
                    decimalScale={6}
                    displayDecimalScale={2}
                    expressionButton={{
                        value: formulaExpression ?? '',
                        title: t('common.formula'),
                        disabled,
                        loadParameters: () => getElementExprParams(),
                        onChange: handleFormulaChange,
                        onValidate: allParameters && parameterKey ? buildCircularReferenceValidator(allParameters, parameterKey, t) : undefined,
                    }}
                />
                {hasPriceError && <InputError error={{ type: 'required' }} />}
            </Col>
            <Col xs={12} md={4} lg={3} xxl={2}>
                <SelectInput
                    options={units}
                    value={item.unitId ?? null}
                    onChange={onChangeUnit}
                    disabled={disabled}
                    hasError={hasUnitError}
                />
                {hasUnitError && <InputError error={{ type: 'required' }} />}
            </Col>
        </Row>
    );
}
